// The cart engine: page-agnostic state over Shopify's cart. No DOM in here, so it is unit-testable.
//  - Keeps only the Shopify cart id (in `storage`), so the cart survives refreshes, tabs and visits.
//  - Every change goes to Shopify; the UI renders Shopify's answer (real prices, stock, discounts).
//  - Changes run one at a time, in order, so quick double-clicks can't race each other.
//  - A missing or finished cart (expired, or the order was placed) is replaced with a fresh one.
import { normalizeCart, toGid, NetworkError } from './shopify.js';

export const MAX_QTY = 10;

export function createCartStore({ transport, storage, key = 'patronus.cart.v2', discountCodes: initialCodes = [] }) {
  let discountCodes = initialCodes;
  let state = { status: 'idle', cart: null, error: null, notices: [] };
  const subs = new Set();
  const set = (patch) => { state = { ...state, ...patch }; subs.forEach(fn => fn(state)); };
  // storage can throw or be wiped (private mode, blocked site data): keep a copy in memory too
  let memId = null;
  const readId = () => { try { return storage.getItem(key) ?? memId; } catch { return memId; } };
  const writeId = (id) => { memId = id || null; try { id ? storage.setItem(key, id) : storage.removeItem(key); } catch {} };

  // run mutations strictly in order
  let queue = Promise.resolve();
  const serial = (job) => { const p = queue.then(job, job); queue = p.catch(() => {}); return p; };

  const notices = (r) => [...(r.userErrors || []), ...(r.warnings || [])].map(e => ({ code: e.code, message: e.message }));

  function accept(cart, extra = []) {
    const c = normalizeCart(cart);
    if (c) writeId(c.id);
    set({ status: 'ready', cart: c, error: null, notices: extra });
    return c;
  }

  async function fetchCart() {
    const id = readId();
    if (!id) return null;
    const data = await transport.run('get', { id });
    if (!data.cart) { writeId(null); return null; }   // expired or checked out
    return data.cart;
  }

  async function load() {
    set({ status: 'loading' });
    try { const c = await fetchCart(); return accept(c); }
    catch (e) { set({ status: 'error', error: e }); return null; }
  }

  // Shopify answers a change to an expired cart by starting a new cart with the change applied
  // (plus a "cart does not exist" error). That is what we want, so that error is dropped.
  const STALE = /cart does not exist/i;
  const tidy = (r) => ({ ...r, userErrors: (r.userErrors || []).filter(e => !STALE.test(e.message)) });
  async function withCart(mutate, createInput) {
    const id = readId();
    if (id) { const r = await mutate(id); if (r.cart || !(r.userErrors || []).some(e => STALE.test(e.message))) return tidy(r); writeId(null); }
    const input = { ...createInput }; if (discountCodes.length) input.discountCodes = discountCodes;
    return (await transport.run('create', { input })).cartCreate;
  }

  const BLOCKING = /STOCK|NOT_ENOUGH|UNAVAILABLE|MAX_QTY|INVALID/;
  const change = (fn) => serial(async () => {
    set({ status: 'updating' });
    try {
      const r = tidy(await fn());
      const ns = notices(r);
      let cart = r.cart;
      if (!cart && readId()) cart = await fetchCart();   // keep showing the real cart after a refused change
      const c = accept(cart || null, ns);
      return { ok: !!c && !ns.some(n => BLOCKING.test(n.code || '')), cart: c, notices: ns };
    } catch (e) { set({ status: state.cart ? 'ready' : 'error', error: e }); return { ok: false, error: e, notices: [] }; }
  });

  function add(lines) {
    const clean = lines.filter(l => l.variantId && l.quantity > 0).map(l => ({ merchandiseId: toGid(l.variantId), quantity: Math.min(MAX_QTY, l.quantity), ...(l.attributes ? { attributes: Object.entries(l.attributes).map(([k, v]) => ({ key: k, value: String(v) })) } : {}) }));
    if (!clean.length) return Promise.resolve({ ok: false, notices: [{ code: 'EMPTY', message: 'Nothing to add' }] });
    return change(async () => {
      // respect the per-line cap across repeat adds of the same variant
      const capped = clean.map(l => { const have = state.cart?.lines.find(x => toGid(x.variantId) === l.merchandiseId && !x.attributes._set)?.quantity || 0; return { ...l, quantity: Math.max(0, Math.min(l.quantity, MAX_QTY - have)) }; }).filter(l => l.quantity > 0);
      if (!capped.length) return { cart: (await fetchCart()), warnings: [{ code: 'MAX_QTY', message: `You can have up to ${MAX_QTY} of each piece` }] };
      return withCart(async (id) => (await transport.run('add', { id, lines: capped })).cartLinesAdd, { lines: capped });
    });
  }

  function setQuantity(lineId, quantity) {
    const q = Math.max(0, Math.min(MAX_QTY, quantity | 0));
    return change(async () => {
      const id = readId(); if (!id) return { cart: null };
      return q === 0 ? (await transport.run('remove', { id, lineIds: [lineId] })).cartLinesRemove
                     : (await transport.run('update', { id, lines: [{ id: lineId, quantity: q }] })).cartLinesUpdate;
    });
  }
  const remove = (lineId) => setQuantity(lineId, 0);

  // set or clear attributes on one line (e.g. the "Card message" for that piece's story card).
  // Shopify replaces a line's attributes as a whole, so the others are carried over; '' removes a key.
  function setLineAttributes(lineId, changes) {
    return change(async () => {
      const id = readId(); if (!id) return { cart: null };
      const line = state.cart?.lines.find(l => l.id === lineId);
      const merged = { ...(line?.attributes || {}), ...changes };
      const attributes = Object.entries(merged).filter(([, v]) => v !== '' && v != null).map(([key, value]) => ({ key, value: String(value) }));
      return (await transport.run('update', { id, lines: [{ id: lineId, attributes }] })).cartLinesUpdate;
    });
  }

  function setDiscountCodes(codes) {
    return change(async () => { const id = readId(); if (!id) return { cart: null }; return (await transport.run('discounts', { id, codes })).cartDiscountCodesUpdate; });
  }

  // codes to carry on every cart from now on (e.g. a welcome code): applied now if there's a cart,
  // otherwise when the first piece goes in
  function useCodes(codes) {
    discountCodes = [...new Set(codes)];
    return readId() ? setDiscountCodes(discountCodes) : Promise.resolve({ ok: true, cart: state.cart, notices: [] });
  }

  // another tab changed the cart, or we came back from checkout
  const refresh = () => serial(load);

  return {
    get state() { return state; },
    subscribe(fn) { subs.add(fn); fn(state); return () => subs.delete(fn); },
    load, refresh, add, setQuantity, remove, setLineAttributes, setDiscountCodes, useCodes,
    get codes() { return discountCodes; },
    cartId: readId, key,
  };
}
export { NetworkError };
